// Service pour gérer les données de l'entreprise

import { apiFetch } from './httpClient';

/**
 * Interface pour les données de l'entreprise
 */
export interface Entreprise {
  id?: string;
  nom?: string;
  adresse?: string;
  codePostal?: string;
  ville?: string;
  pays?: string;
  telephone?: string;
  email?: string;
  siteWeb?: string;
  siret?: string;
  tva?: string;
  logo?: string;
  soumisATVA?: boolean;
  tauxTVA?: number;
  couleurPrimaire?: string;
  couleurSecondaire?: string;
  piedPage?: string;
  conditionsPaiement?: string;
  mentionsLegales?: string;
  has_cgv?: boolean;
  cgvFilename?: string;
  cgvMime?: string;
  cgvSize?: number;
  cgvUploadedAt?: string | null;
  seeded_fields?: string[];
}

/**
 * Classe pour gérer les données de l'entreprise
 */
class EntrepriseService {

  /**
   * Récupère les données de l'entreprise
   */
  async getEntreprise(): Promise<Entreprise> {
    try {
      return await apiFetch<Entreprise>('/entreprise');
    } catch (error) {
      console.error("Erreur lors de la récupération des données de l'entreprise:", error);
      // Retourner des données de démonstration en cas d'erreur
      return this.getEntrepriseDemo();
    }
  }

  /**
   * Sauvegarde les données de l'entreprise
   */
  async saveEntreprise(entreprise: Partial<Entreprise>): Promise<Entreprise> {
    try {
      return await apiFetch<Entreprise>('/entreprise', {
        method: 'POST',
        body: JSON.stringify(entreprise),
      });
    } catch (error) {
      console.error("Erreur lors de la sauvegarde des données de l'entreprise:", error);
      // Simuler la sauvegarde en cas d'erreur
      return entreprise;
    }
  }

  /**
   * Confirme les valeurs par défaut (vide seeded_fields)
   */
  async confirmDefaults(): Promise<Entreprise> {
    try {
      return await apiFetch<Entreprise>('/entreprise/confirm_defaults', {
        method: 'POST',
      });
    } catch (error) {
      console.error('Erreur lors de la confirmation des valeurs par défaut:', error);
      throw error;
    }
  }

  /**
   * Confirme un champ spécifique (le retire de seeded_fields)
   */
  async confirmField(fieldName: string): Promise<Entreprise> {
    try {
      return await apiFetch<Entreprise>('/entreprise/confirm_field', {
        method: 'POST',
        body: JSON.stringify({ field: fieldName }),
      });
    } catch (error) {
      console.error(`Erreur lors de la confirmation du champ ${fieldName}:`, error);
      throw error;
    }
  }

  /**
   * Retourne des données d'entreprise de démonstration
   */
  private getEntrepriseDemo(): Entreprise {
    return {
      id: 'entreprise-1',
      nom: 'SelleriePro',
      adresse: '123 Avenue de la Marine',
      codePostal: '13000',
      ville: 'Marseille',
      pays: 'France',
      telephone: '04 91 XX XX XX',
      email: 'contact@selleriepro.fr',
      siteWeb: 'www.selleriepro.fr',
      siret: '123 456 789 00012',
      tva: 'FR 12 123456789',
      logo: '/logo.png',
      couleurPrimaire: '#0077b6',
      couleurSecondaire: '#90e0ef',
      piedPage: 'SelleriePro - Tous droits reserves',
      conditionsPaiement: 'Paiement a 30 jours fin de mois',
      mentionsLegales: 'SelleriePro - SARL au capital de 10 000 EUR - RCS Marseille 123 456 789',
      has_cgv: false,
      cgvFilename: '',
      cgvMime: '',
      cgvSize: 0,
      cgvUploadedAt: null,
      seeded_fields: [],
    };
  }

  /**
   * Upload ou remplace les CGV (PDF uniquement)
   */
  async uploadCgv(file: File): Promise<Entreprise> {
    const formData = new FormData();
    formData.append('file', file);
    return apiFetch<Entreprise>('/entreprise/cgv', {
      method: 'POST',
      body: formData,
    });
  }

  /**
   * Supprime les CGV stockees
   */
  async deleteCgv(): Promise<Entreprise> {
    return apiFetch<Entreprise>('/entreprise/cgv', {
      method: 'DELETE',
    });
  }
}

// Exporter une instance unique du service
export const entrepriseService = new EntrepriseService();
